import React, { useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import clsx from "clsx";
import dayjs from "dayjs";
import { t } from "i18next";
import { IDocument } from "../../types/documents";
import { getDocumentsStatusIcon } from "../../utils/helpers/statusIcons";
import Tooltip from "../../components/ui/tooltip/Tooltip";


interface Props {
  documents: IDocument[];
  hiddenColumns?: string[];
  getRowClass?: (params: any) => string;
  openCloseSidebarDocuments: (document: IDocument | null) => void;
  openCloseModalPdfDocuments: (document: IDocument | null) => void;
  grid?: boolean
}


const DocumentsTable = (props: Props) => {
  const {
    documents,
    hiddenColumns = [],
    getRowClass = () => [],
    openCloseSidebarDocuments,
    openCloseModalPdfDocuments,
    grid = false
  } = props;

  const gridRef = useRef<any>(null);

  const columnDefs = useMemo(() => {
    let cols = [
      {
        field: "name",
        headerName: t("screens.documents.tableHeaders.type"),
        tooltipField: "name",
        cellRenderer: (props: any) => {
          return (
            <div
              onClick={() =>
                props.data.uploadedDocument
                  ? openCloseModalPdfDocuments(props.data)
                  : null
              }
              className={clsx({
                "underline cursor-pointer font-semibold":
                  props.data.uploadedDocument,
              })}
            >
              {props.value}
            </div>
          );
        },
        flex: 1,
      },
      {
        field: "documentSubType",
        headerName: t("screens.documents.tableHeaders.subType"),
        tooltipField: "documentSubType",
        cellRenderer: (props: any) => {
          return (
            <div
              onClick={() =>
                props.data.uploadedDocument
                  ? openCloseModalPdfDocuments(props.data)
                  : null
              }
              className={clsx({
                "underline cursor-pointer font-semibold":
                  props.data.uploadedDocument,
              })}
            >
              {props.value}
            </div>
          );
        },
        flex: 1,
      },
      {
        field: "documentName",
        headerName: t("screens.documents.tableHeaders.name"),
        tooltipField: "documentName",
        flex: 1,
      },
      {
        field: "uploadedOn",
        headerName: t("screens.documents.tableHeaders.uploaded"),
        cellRenderer: ({ data }: any) => {
          return (
            <>
              {!!data.uploadedOn && (
                <div>{dayjs(data.uploadedOn).format("DD/MM/YYYY")}</div>
              )}

              {!!!data.uploadedOn && <div>-</div>}
            </>
          );
        },
        flex: 0.5,
      },
      {
        field: "validUntil",
        headerName: t("screens.documents.tableHeaders.validUntil"),
        cellRenderer: ({ data }: any) => {
          return (
            <>
              {!!data.validUntil && (
                <div>{dayjs(data.validUntil).format("DD/MM/YYYY")}</div>
              )}

              {!!!data.validUntil && <div>-</div>}
            </>
          );
        },
        flex: 0.5,
      },
      {
        field: "status",
        headerName: t("screens.documents.tableHeaders.status"),
        cellRenderer: ({ data }: any) => {
          return (
            <div className="flex items-center ">
              <div>{getDocumentsStatusIcon(data.statusCode)}</div>
              <div className="ml-1">{data.status}</div>
            </div>
          );
        },
        flex: 0.5,
      },
      {
        field: "status",
        headerName: t("screens.documents.tableHeaders.action"),
        cellRenderer: ({ data }: any) => {

          // Se action === 0, non mostrare nulla
          if (data.action === 0) {
            return null;
          }

          return (
            <>
              {
                data.isExpiring === false &&
                data.mustBeAccepted === false && (
                  <div
                    title={
                      (data.action === 3
                        ? t("screens.documents.uploadRequested")
                        : t("screens.documents.uploadNotMandatory")) || ""
                    }
                    onClick={() => openCloseSidebarDocuments(data)}
                    className="cursor-pointer flex items-center font-bold text-red"
                  >
                    <div className="mr-1">
                      <svg
                        width="16"
                        height="17"
                        viewBox="0 0 16 17"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M7.33337 10.5H8.66671V6.49996H10.6667L8.00004 3.16663L5.33337 6.49996H7.33337V10.5Z"
                          className="fill-red"
                        />
                        <path
                          d="M13.3334 12.5H2.66671V7.83337H1.33337V12.5C1.33337 13.2354 1.93137 13.8334 2.66671 13.8334H13.3334C14.0687 13.8334 14.6667 13.2354 14.6667 12.5V7.83337H13.3334V12.5Z"
                          className="fill-red"
                        />
                      </svg>
                    </div>
                    <div>
                      {(data.action === 3 || data.action === 4)
                        ? t("screens.documents.uploadRequested")
                        : data.action === 1 ?
                          t("screens.documents.uploadNewVersion")
                          : t("screens.documents.uploadNotMandatory")}
                    </div>
                  </div>
                )}
              {data.mustBeAccepted && (
                <div
                  onClick={() => openCloseSidebarDocuments(data)}
                  className="cursor-pointer font-bold text-red"
                >
                  <div>{t("screens.documents.acceptanceRequest")}</div>
                </div>
              )}
              {data.isExpiring === true && (
                <div
                  onClick={() => openCloseSidebarDocuments(data)}
                  className="cursor-pointer flex items-center font-bold text-red"
                >
                  <div className="mr-1">
                    <svg
                      width="16"
                      height="17"
                      viewBox="0 0 16 17"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7.33337 10.5H8.66671V6.49996H10.6667L8.00004 3.16663L5.33337 6.49996H7.33337V10.5Z"
                        className="fill-red"
                      />
                      <path
                        d="M13.3334 12.5H2.66671V7.83337H1.33337V12.5C1.33337 13.2354 1.93137 13.8334 2.66671 13.8334H13.3334C14.0687 13.8334 14.6667 13.2354 14.6667 12.5V7.83337H13.3334V12.5Z"
                        className="fill-red"
                      />
                    </svg>
                  </div>
                  <div>{t("screens.documents.updateRequested")}</div>
                </div>
              )}
              {data.action === 4 && (
                <div
                  onClick={() => openCloseSidebarDocuments(data)}
                  className="cursor-pointer flex items-center font-bold text-red"
                >
                  <div className="mr-1">
                    <svg
                      width="16"
                      height="17"
                      viewBox="0 0 16 17"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7.33337 10.5H8.66671V6.49996H10.6667L8.00004 3.16663L5.33337 6.49996H7.33337V10.5Z"
                        className="fill-red"
                      />
                      <path
                        d="M13.3334 12.5H2.66671V7.83337H1.33337V12.5C1.33337 13.2354 1.93137 13.8334 2.66671 13.8334H13.3334C14.0687 13.8334 14.6667 13.2354 14.6667 12.5V7.83337H13.3334V12.5Z"
                        className="fill-red"
                      />
                    </svg>
                  </div>
                  <div>{t("screens.documents.uploadTemplate")}</div>
                </div>
              )}
              {data.action === 1 && (
                <div
                  onClick={() => openCloseSidebarDocuments(data)}
                  className="cursor-pointer flex items-center"
                >
                  <div className="mr-1">
                    <svg
                      width="16"
                      height="17"
                      viewBox="0 0 16 17"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7.33337 10.5H8.66671V6.49996H10.6667L8.00004 3.16663L5.33337 6.49996H7.33337V10.5Z"
                        className="fill-red"
                      />
                      <path
                        d="M13.3334 12.5H2.66671V7.83337H1.33337V12.5C1.33337 13.2354 1.93137 13.8334 2.66671 13.8334H13.3334C14.0687 13.8334 14.6667 13.2354 14.6667 12.5V7.83337H13.3334V12.5Z"
                        className="fill-red"
                      />
                    </svg>
                  </div>
                  <div>{t("screens.documents.uploadNewVersion")}</div>
                </div>
              )}
            </>
          );
        },
        flex: 1,
      },
      {
        field: "notes",
        headerName: t("screens.documents.tableHeaders.note"),
        cellRenderer: ({ data }: any) => {
          return (
            <div className="flex items-center h-full">
              {data.action === 3 &&
                data.isExpiring === false &&
                data.mustBeAccepted === false && (
                  <div className="relative mr-2">
                    <div
                      className="cursor-pointer"
                      id={`tooltip-documents-uploadRequested-trigger-${data.id}`}
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M18 9C18 13.879 13.879 18 9 18C4.121 18 0 13.879 0 9C0 4.121 4.121 0 9 0C13.879 0 18 4.121 18 9ZM16 9C16 5.206 12.794 2 9 2C5.206 2 2 5.206 2 9C2 12.794 5.206 16 9 16C12.794 16 16 12.794 16 9ZM10 10V5H8V10H10ZM10 13V11H8V13H10Z"
                          fill="#CF0A2C"
                        />
                      </svg>
                    </div>

                    <Tooltip
                      id={`tooltip-documents-uploadRequested-${data.id}`}
                      triggerId={`tooltip-documents-uploadRequested-trigger-${data.id}`}
                      title={t("screens.documents.uploadRequested")}
                      description={t("screens.documents.uploadRequestedText")}
                      positionX="left"
                      positionY="top"
                      mobilePositionX="left"
                      mobilePositionY="top"
                      isModal={true}
                    />
                  </div>
                )}

              {data.isExpiring === true && (
                <div className="relative mr-2">
                  <div
                    className="cursor-pointer"
                    id={`tooltip-documents-updateRequest-trigger-${data.id}`}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M18 9C18 13.879 13.879 18 9 18C4.121 18 0 13.879 0 9C0 4.121 4.121 0 9 0C13.879 0 18 4.121 18 9ZM16 9C16 5.206 12.794 2 9 2C5.206 2 2 5.206 2 9C2 12.794 5.206 16 9 16C12.794 16 16 12.794 16 9ZM10 10V5H8V10H10ZM10 13V11H8V13H10Z"
                        fill="#CF0A2C"
                      />
                    </svg>
                  </div>

                  <Tooltip
                    id={`tooltip-documents-updateRequest-${data.id}`}
                    triggerId={`tooltip-documents-updateRequest-trigger-${data.id}`}
                    title={t("screens.documents.updateRequested")}
                    description={t("screens.documents.updateRequestedText", {
                      days_before_blocking: data.daysBeforeBlocking,
                    })}
                    positionX="left"
                    positionY="top"
                    mobilePositionX="left"
                    mobilePositionY="top"
                    isModal={true}
                  />
                </div>
              )}

              {data.action === 4 && (
                <div className="relative mr-2">
                  <div
                    className="cursor-pointer"
                    id={`tooltip-documents-uploadTemplate-trigger-${data.id}`}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M18 9C18 13.879 13.879 18 9 18C4.121 18 0 13.879 0 9C0 4.121 4.121 0 9 0C13.879 0 18 4.121 18 9ZM16 9C16 5.206 12.794 2 9 2C5.206 2 2 5.206 2 9C2 12.794 5.206 16 9 16C12.794 16 16 12.794 16 9ZM10 10V5H8V10H10ZM10 13V11H8V13H10Z"
                        fill="#CF0A2C"
                      />
                    </svg>
                  </div>

                  <Tooltip
                    id={`tooltip-documents-uploadTemplate-${data.id}`}
                    triggerId={`tooltip-documents-uploadTemplate-trigger-${data.id}`}
                    title={t("screens.documents.uploadTemplate")}
                    description={t("screens.documents.uploadTemplateText")}
                    positionX="left"
                    positionY="top"
                    mobilePositionX="left"
                    mobilePositionY="top"
                    isModal={true}
                  />
                </div>
              )}

              {!!data.notes && (
                <div className="relative mr-2">
                  <div
                    className="cursor-pointer"
                    id={`tooltip-documents-note-trigger-${data.id}`}
                  >
                    <svg
                      width="14"
                      height="15"
                      viewBox="0 0 14 15"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12.1852 0.833374H1.80745C0.992633 0.833374 0.333374 1.50004 0.333374 2.31486L0.340781 12.6852C0.340781 13.5 1.00004 14.1667 1.81486 14.1667H9.22226L13.6667 9.72226V2.31486C13.6667 1.50004 13 0.833374 12.1852 0.833374ZM3.29634 4.53708H10.7037V6.01856H3.29634V4.53708ZM7.00004 8.98152H3.29634V7.50004H7.00004V8.98152ZM8.48152 13.0556V8.98152H12.5556L8.48152 13.0556Z"
                        fill="#CF0A2C"
                      />
                    </svg>
                  </div>

                  <Tooltip
                    id={`tooltip-documents-note-${data.id}`}
                    triggerId={`tooltip-documents-note-trigger-${data.id}`}
                    title={t("screens.documents.note")}
                    description={data.notes}
                    positionX="left"
                    positionY="top"
                    mobilePositionX="left"
                    mobilePositionY="top"
                    isModal={true}
                  />
                </div>
              )}
            </div>
          );
        },
        flex: 0.5,
      },
    ];

    cols = cols.filter((col: any) => {
      return !hiddenColumns.includes(col.field);
    });

    return cols;
  }, [documents, hiddenColumns]);

  const onGridReady = () => {
    if (gridRef.current) {
      gridRef.current.api.setDomLayout("autoHeight");
    }
  };

  return (
    <div className="ag-theme-alpine" style={{ width: "100%", height: "100%" }}>
      <AgGridReact
        ref={gridRef}
        rowData={documents}
        columnDefs={columnDefs}
        defaultColDef={{
          flex: 1,
        }}
        onGridReady={onGridReady}
        suppressRowClickSelection
        suppressCellFocus
        suppressMovableColumns
        getRowClass={getRowClass}
      />
    </div>
  );
};

export default DocumentsTable;
